import { create } from "zustand";
import { persist } from "zustand/middleware";
import type {
  Coordinates,
  Plan,
  PlanInput,
  PlanResult,
} from "../domain/models";

interface AppState {
  anonymousSessionId: string;
  authenticated: boolean;
  originLabel: string;
  draft: PlanInput;
  result: PlanResult | null;
  selectedPlanId: string | null;
  setAuthenticated(value: boolean): void;
  setOrigin(coordinates: Coordinates, label: string): void;
  useCity(cityId: string): void;
  setDraft(value: Partial<PlanInput>): void;
  setResult(value: PlanResult | null): void;
  selectPlan(id: string): void;
  updatePlan(plan: Plan): void;
  clear(): void;
}
const initialDraft: PlanInput = {
  cityId: "beirut",
  date: new Date(Date.now() + 86400000).toISOString().slice(0, 10),
  startTime: "10:00",
  availableDurationMinutes: 240,
  adults: 2,
  children: 0,
  childDetails: [],
  maximumPerPersonBudgetMinor: undefined,
  interests: [],
  excludedActivityCategories: [],
  generationMode: "standard",
  requiresAccessible: false,
  requiredAmenities: [],
};
export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      anonymousSessionId: crypto.randomUUID(),
      authenticated: false,
      originLabel: "Beirut",
      draft: initialDraft,
      result: null,
      selectedPlanId: null,
      setAuthenticated: (authenticated) => set({ authenticated }),
      setOrigin: (coordinates, originLabel) =>
        set((state) => ({
          originLabel,
          draft: {
            ...state.draft,
            cityId: undefined,
            originLatitude: coordinates.latitude,
            originLongitude: coordinates.longitude,
          },
        })),
      useCity: (cityId) =>
        set((state) => ({
          originLabel: "Selected city",
          draft: {
            ...state.draft,
            cityId,
            originLatitude: undefined,
            originLongitude: undefined,
          },
        })),
      setDraft: (patch) =>
        set((state) => ({ draft: { ...state.draft, ...patch } })),
      setResult: (result) => set({ result }),
      selectPlan: (selectedPlanId) => set({ selectedPlanId }),
      updatePlan: (plan) =>
        set((state) => ({
          result: state.result
            ? {
                ...state.result,
                plans: state.result.plans.map((item) =>
                  item.id === plan.id ? plan : item,
                ),
              }
            : null,
        })),
      clear: () =>
        set({ authenticated: false, result: null, selectedPlanId: null }),
    }),
    {
      name: "spot.public-state.v2",
      partialize: (state) => ({
        anonymousSessionId: state.anonymousSessionId,
        originLabel: state.originLabel,
        draft: state.draft,
        result: state.result,
        selectedPlanId: state.selectedPlanId,
      }),
    },
  ),
);
